import { Layer } from "@carbon/react";
import { FC } from "react";

import InlineError from "src/components/Errors/InlineError.tsx";
import { Drawer } from "src/components/Modal";
import { DynamicViewRenderer } from "src/dynamic-view-renderer/DynamicViewRenderer.tsx";
import { useDynamicViewSchema } from "src/dynamic-view-renderer/DynamicViewSchemaProvider/DynamicViewSchemaProvider.tsx";
import { useGetAlertById } from "src/graphql/anti-fraud/queries/alerts/GetAlertByIdQuery.ts";
import { useT } from "src/internationalization";

import { GRAPH_SIDE_PANEL_WIDTH_REM } from "../graph.constants";
import { GraphAlertNodeData } from "../graph.types.ts";

type GraphAlertNodePassiveModalProps = {
  data: GraphAlertNodeData;
  onClose: () => void;
};

export const GraphAlertNodePassiveModal: FC<GraphAlertNodePassiveModalProps> = ({
  data: { label, serverId },
  onClose,
}): JSX.Element => {
  const t = useT();
  const { data: alert, error, loading, refetch } = useGetAlertById({ id: serverId });
  const uiSchema = useDynamicViewSchema();
  const viewConfiguration = uiSchema.graph.getElementView("case-alert");
  return (
    <Drawer
      layer={2}
      width={`${GRAPH_SIDE_PANEL_WIDTH_REM}rem`}
      className="[&_.cds--modal-content]:overflow-y-hidden"
      title={label}
      onClose={onClose}
      closeOnOutsideClick={false}
      loading={loading}
      error={
        error
          ? {
              title: error.title,
              subtitle: error.message,
            }
          : undefined
      }
    >
      <Layer level={1} className="h-full overflow-hidden flex flex-col">
        {viewConfiguration && alert && alert.currentCase && (
          <DynamicViewRenderer
            viewConfiguration={viewConfiguration}
            data={{
              widgetsData: {
                basic: {
                  item: {
                    propertyValues: alert.currentCase.properties,
                    elements: alert.currentCase.elements,
                    blacklistedIdentifiers: alert.currentCase.blacklistedIdentifiers,
                    code: alert.typeCode,
                    type: "case",
                  },
                  propertyValueRendererParams: {
                    CaseLink: {
                      id: alert.currentCase?.id.toString() ?? "",
                      caseTypeCode: alert.currentCase.typeCode,
                    },
                  },
                },
                rules: {
                  scenarioExecutionResult: alert?.scenarioExecutionResult,
                },
                alertWorkflow: {
                  alert,
                  refetch,
                },
                chronology: {
                  alert,
                },
                relatedCases: {
                  elements: alert.currentCase.elements,
                  propertyValues: alert.currentCase.properties,
                  caseId: alert.currentCase?.id,
                  alertId: alert.id,
                  snapshotCreation: null,
                },
              },
            }}
            isStateful={false}
          />
        )}
        {!viewConfiguration && <InlineError message={t("general.configuration.error")} />}
      </Layer>
    </Drawer>
  );
};
